"use client";

import { useEffect, useState } from "react";
import { FormStatus } from "@/components/ui/Form";
import {
  fetchPrivateMessages,
  type PrivateMessage,
  type PrivateMessagesPagination,
} from "@/lib/api/c-private-messages";
import { useLocale } from "@/lib/i18n/context";
import { getMessages } from "@/lib/i18n";
import styles from "./MessagesList.module.css";

/**
 * Reuse'owalny komponent listy wiadomości — używany przez inbox i sent view.
 * Różnice per folder:
 *   - inbox: `sender_login` to OD kogo, `is_read` decyduje o bold/unread style
 *   - sent: `sender_login` faktycznie to odbiorca (TODO: rozróżnić), `is_read` null
 *
 * Layout: tabela 3-kolumnowa (sender/title-preview/date) z pagination poniżej.
 */
export function MessagesList({ folder }: { folder: "inbox" | "sent" }) {
  const t = getMessages(useLocale());
  const [messages, setMessages] = useState<PrivateMessage[]>([]);
  const [pagination, setPagination] = useState<PrivateMessagesPagination | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { messages: rows, pagination: p } = await fetchPrivateMessages(folder, page, 20);
        if (cancelled) return;
        setMessages(rows);
        setPagination(p);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [folder, page]);

  if (loading) {
    return (
      <div className={styles.list} aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className={styles.itemSkeleton}>
            <span className={`skeleton-base ${styles.itemSkeletonSender}`} />
            <span className={`skeleton-base ${styles.itemSkeletonTitle}`} />
            <span className={`skeleton-base ${styles.itemSkeletonDate}`} />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return <FormStatus variant="error">{error}</FormStatus>;
  }

  if (messages.length === 0) {
    return (
      <p className={styles.empty}>
        {folder === "inbox" ? "Brak wiadomości w skrzynce odbiorczej." : "Nie wysłałeś jeszcze żadnych wiadomości."}
      </p>
    );
  }

  return (
    <div className={styles.list}>
      {messages.map((msg) => (
        <article
          key={msg.id}
          className={msg.is_read === false ? styles.itemUnread : styles.item}
        >
          <span className={styles.itemSender}>{msg.sender_login || "(nieznany)"}</span>
          <div className={styles.itemBody}>
            <p className={styles.itemTitle}>{msg.title || "(bez tytułu)"}</p>
            {msg.text_preview && (
              <p className={styles.itemPreview}>{msg.text_preview}</p>
            )}
          </div>
          <time className={styles.itemDate} dateTime={msg.sent_date}>
            {formatDate(msg.sent_date)}
          </time>
        </article>
      ))}

      {pagination && pagination.total_pages > 1 && (
        <nav className={styles.pagination} aria-label="Paginacja">
          <button
            type="button"
            className={styles.pagBtn}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            ← Poprzednia
          </button>
          <span className={styles.pagInfo}>
            Strona {pagination.page} z {pagination.total_pages} (łącznie {pagination.total})
          </span>
          <button
            type="button"
            className={styles.pagBtn}
            onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
            disabled={page >= pagination.total_pages}
          >
            {t.common.paginationNext}
          </button>
        </nav>
      )}
    </div>
  );
}

/* Polski format daty — `2026-05-22 14:36:53` -> `22.05.2026 14:36`. */
function formatDate(iso: string): string {
  if (!iso) return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/);
  if (!m) return iso;
  return `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}`;
}
